import React from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import dayjs from "dayjs";
import { tokens } from "../theme";
import { DealSummary } from "Models/deal-summary";

const ALL_STAGES = "__ALL__";

interface DealsTableProps {
  deals: DealSummary[];
  totalCount: number;
  page: number;
  pageSize: number;
  loading?: boolean;
  stageOptions?: string[];
  selectedStages: string[];
  onStagesChange?: (stages: string[]) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onExportCsv?: () => void;
  exporting?: boolean;
  exportError?: string | null;
}

interface Column {
  label: string;
  key: keyof DealSummary;
  align?: "left" | "right" | "center";
  format?: (value: DealSummary[keyof DealSummary], row: DealSummary) => string;
}

const formatDate = (value: unknown): string => {
  if (!value) return "-";
  const d = dayjs(value as string | number | Date);
  return d.isValid() ? d.format("YYYY-MM-DD") : "-";
};

const formatNumber = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "-";
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n.toLocaleString() : "-";
};

const formatCurrency = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "-";
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return "-";
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
};

const formatDays = (value: unknown): string => {
  if (value === null || value === undefined) return "-";
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return "-";
  return `${n} day${n === 1 ? "" : "s"}`;
};

const columns: Column[] = [
  { label: "Deal Name", key: "name", align: "left" },
  { label: "Current Deal Stage", key: "stage", align: "left" },
  { label: "# of Days in Current Stage", key: "daysInCurrentStage", align: "right", format: formatDays },
  { label: "# of Sales Activities", key: "noOfSalesActivities", align: "right", format: formatNumber },
  { label: "Time Since Last Activity", key: "timeSinceLastActivity", align: "right", format: formatDays },
  { label: "Amount", key: "amount", align: "right", format: formatCurrency },
  { label: "Create Date", key: "createDate", align: "left", format: formatDate },
  { label: "Close Date", key: "closeDate", align: "left", format: formatDate },
  { label: "Pipeline", key: "pipeline", align: "left" },
  { label: "Deal Owner", key: "owner", align: "left" },
  { label: "# of Contacts", key: "noOfContacts", align: "right", format: formatNumber },
  { label: "Company", key: "associatedCompany", align: "left" },
];

const escapeCsv = (value: string): string => {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

const formatCell = (column: Column, deal: DealSummary): string => {
  const raw = deal[column.key];
  if (column.format) return column.format(raw, deal);
  if (raw === null || raw === undefined || raw === "") return "";
  return String(raw);
};

export const buildDealsCsv = (rows: DealSummary[]): string => {
  const header = columns.map((c) => escapeCsv(c.label)).join(",");
  const body = rows
    .map((deal) => columns.map((c) => escapeCsv(formatCell(c, deal))).join(","))
    .join("\n");
  return body ? `${header}\n${body}` : header;
};

export const triggerCsvDownload = (csv: string, filename: string) => {
  // Prefix BOM so Excel detects UTF-8 correctly.
  const blob = new Blob([`﻿${csv}`], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const DealsTable: React.FC<DealsTableProps> = ({
  deals,
  totalCount,
  page,
  pageSize,
  loading,
  stageOptions,
  selectedStages,
  onStagesChange,
  onPageChange,
  onPageSizeChange,
  onExportCsv,
  exporting,
  exportError,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const showStageFilter = !!onStagesChange && !!stageOptions && stageOptions.length > 0;

  return (
    <Card
      variant="outlined"
      sx={{ overflow: "hidden", p: 3, my: 2, ml: 2, mr: 3, bgcolor: "#FFFFFF" }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, px: 3 }}>
        <Typography
          variant="subtitle1"
          color={colors.gray[200]}
          bgcolor={colors.gray[900]}
          fontWeight="bold"
          sx={{ p: 1, px: 3, display: "inline-block" }}
        >
          Deals
        </Typography>
        {showStageFilter && (
          <FormControl size="small" sx={{ minWidth: 200, ml: 1 }}>
            {(() => {
              const allSelected = stageOptions!.length > 0 && selectedStages.length === stageOptions!.length;
              const someSelected = selectedStages.length > 0 && !allSelected;
              return (
                <Select
                  multiple
                  value={selectedStages}
                  onChange={(e) => {
                    const value = e.target.value as string[];
                    if (value.includes(ALL_STAGES)) {
                      onStagesChange?.(allSelected ? [] : stageOptions!);
                      return;
                    }
                    onStagesChange?.(value);
                  }}
                  displayEmpty
                  renderValue={() => (
                    <Typography sx={{ fontSize: "0.875rem", color: "#111827" }}>
                      Stage: {selectedStages.length === 0 ? "All" : `${selectedStages.length} selected`}
                    </Typography>
                  )}
                >
                  <MenuItem value={ALL_STAGES}>
                    <Checkbox checked={allSelected} indeterminate={someSelected} />
                    All
                  </MenuItem>
                  {stageOptions!.map((s) => (
                    <MenuItem key={s} value={s}>
                      <Checkbox checked={selectedStages.includes(s)} />
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              );
            })()}
          </FormControl>
        )}
        {loading && <CircularProgress size={24} sx={{ color: colors.primary[500] }} />}
        {onExportCsv && (
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
            {exportError && (
              <Typography sx={{ fontSize: "0.75rem", color: "#B91C1C" }}>
                {exportError}
              </Typography>
            )}
            <Button
              variant="outlined"
              type="button"
              color="primary"
              size="small"
              startIcon={
                exporting ? (
                  <CircularProgress size={14} sx={{ color: "inherit" }} />
                ) : (
                  <FileDownloadOutlinedIcon fontSize="small" />
                )
              }
              sx={{ fontWeight: 700 }}
              onClick={onExportCsv}
              disabled={!!exporting || !!loading}
            >
              Download CSV
            </Button>
          </Box>
        )}
      </Box>

      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ width: "auto", tableLayout: "auto" }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.key as string}
                  align={column.align ?? "left"}
                  sx={{
                    borderBottom: "none",
                    py: 2,
                    px: 2,
                    whiteSpace: "nowrap",
                  }}
                >
                  <Typography sx={{ fontSize: "0.75rem", color: "#6B7280" }}>
                    {column.label}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {deals.length === 0 && !loading && (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{ py: 4, color: "#6B7280" }}
                >
                  <Typography sx={{ fontSize: "0.875rem" }}>No deals match the current filters.</Typography>
                </TableCell>
              </TableRow>
            )}

            {deals.map((deal) => (
              <TableRow
                key={deal.deal_Id}
                sx={{
                  "&:nth-of-type(even)": { bgcolor: "#F9FAFB" },
                  "&:last-child td": { borderBottom: 0 },
                }}
              >
                {columns.map((column) => {
                  const raw = deal[column.key];
                  const display = column.format
                    ? column.format(raw, deal)
                    : raw === null || raw === undefined || raw === ""
                      ? "-"
                      : String(raw);
                  return (
                    <TableCell
                      key={column.key as string}
                      align={column.align ?? "left"}
                      sx={{ py: 1.5, px: 2, whiteSpace: "nowrap" }}
                    >
                      {column.key === "name" ? (
                        <RouterLink
                          to={`/deals/${deal.deal_Id}`}
                          style={{ fontSize: "0.875rem", color: "#1D4ED8", textDecoration: "none" }}
                          onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                          onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
                        >
                          {display}
                        </RouterLink>
                      ) : (
                        <Typography sx={{ fontSize: "0.875rem", color: "#111827" }}>
                          {display}
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, newPage) => onPageChange(newPage)}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onRowsPerPageChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
      />
    </Card>
  );
};

export default DealsTable;
